import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { Text } from "@mantine/core";
import dayjs from "dayjs";
import {
  RiCalendar2Line,
  RiTimeLine,
  RiBox1Line,
  RiServiceLine,
} from "react-icons/ri";
import { Users } from "lucide-react";

import localizedFormat from "dayjs/plugin/localizedFormat";
import useAccountInfo from "@/hooks/useAccountInfo";
import { FiGlobe } from "react-icons/fi";
import { Avatar, Divider } from "@heroui/react";
dayjs.extend(localizedFormat);

type Customer = {
  name: string;
  lastName: string;
  email?: string;
  gsm?: string;
  comment?: string;
  bookedFromForm: boolean;
  attendance?: string;
  extraDetails?: Array<{
    id: string;
    type: "text" | "textarea" | "radio" | "select" | string;
    label: {
      [key: string]: string;
    };
    value:
      | string
      | {
          [key: string]: string;
        };
  }>;
};

type Resource = {
  label: string;
  id: number;
};

type FormattedData = {
  startTime: string;
  endTime: string;
  date: string;
};

type Appointment = {
  formattedData: FormattedData;
  serviceName: string;
  customers: Customer[];
  resources: number[];
  userIds: number[];
  numberOfPeople: number | undefined;
};

type HoverContentProps = {
  appointment: Appointment;
  viewCustomersPermission: boolean;
  resources: Resource[];
  users: User[];
};

type User = {
  userId: number;
  name: string;
  lastName: string;
  fullImageUrl?: string;
};

export const HoverContent = ({
  appointment,
  viewCustomersPermission,
  resources,
  users,
}: HoverContentProps) => {
  const { formattedData, serviceName, customers = [] } = appointment;
  const { accountInfo: { userId: loggedInUserId } = {} } = useAccountInfo();

  const { startTime, endTime, date } = formattedData;

  const { locale } = i18n;
  const appointmentDate = dayjs(date, "DD.MM.YYYY").locale(locale).format("LL");
  const appointmentTime = `${startTime} - ${endTime}`;

  const groupAppointment = appointment.numberOfPeople
    ? appointment.numberOfPeople > 1
    : false;

  return (
    <div className="flex max-w-72 flex-col gap-2 px-2 py-3">
      <div className="flex items-center gap-2">
        <RiCalendar2Line />
        <Text size="sm">{appointmentDate}</Text>
      </div>
      <div className="flex items-center gap-2">
        <RiTimeLine />
        <Text size="sm">{appointmentTime}</Text>
      </div>
      <Divider />
      <div className="flex items-center gap-2">
        <RiServiceLine />
        <Text size="sm">{serviceName}</Text>
      </div>
      {customers.length > 0 &&
        customers[0].attendance &&
        Number(customers[0].attendance) > 1 && (
          <div className="flex items-center gap-2">
            <Users size={16} />
            <Text size="sm" c="dimmed">
              {customers[0].attendance} <Trans>oseb</Trans>
            </Text>
          </div>
        )}
      {appointment.resources.length > 0 && (
        <div className="flex items-center gap-2">
          <RiBox1Line />
          <Text size="sm" c="dimmed">
            {appointment.resources
              .map(
                (resourceId) =>
                  resources.find((resource) => resource.id === resourceId)
                    ?.label,
              )
              .join(", ")}
          </Text>
        </div>
      )}
      {appointment.userIds.map((userId) => {
        const user = users.find((u) => u.userId === userId);
        if (!user) return null;
        return (
          <div key={userId} className="flex items-center gap-2">
            <Avatar size="sm" radius="md" src={user.fullImageUrl}>
              {user.name[0]} {user.lastName?.[0]}
            </Avatar>
            <Text size="sm">
              {user.name} {user.lastName}
            </Text>
          </div>
        );
      })}

      {viewCustomersPermission && (
        <>
          <Divider />
          <div className="mt-2 flex flex-col gap-2">
            {customers.length > 0 ? (
              <>
                {groupAppointment && (
                  <Text size="sm" c="dimmed">
                    <Trans>
                      {`Št. strank: ${customers.length}`} /{" "}
                      {appointment.numberOfPeople}
                    </Trans>
                  </Text>
                )}
                {customers.map((customer, index) => (
                  <div key={`${customer.name}-${customer.lastName}-${index}`}>
                    <div className="flex items-center">
                      <Avatar
                        size="sm"
                        radius="md"
                        color="success"
                        style={{
                          display: "inline-block",
                          marginRight: "8px",
                          textAlign: "center",
                        }}
                      >
                        {customer.name[0]} {customer.lastName[0]}
                      </Avatar>
                      <Text size="sm" style={{ whiteSpace: "nowrap" }}>
                        {customer.name} {customer.lastName}{" "}
                        {customer.bookedFromForm && (
                          <FiGlobe
                            style={{
                              display: "inline-block",
                            }}
                          />
                        )}
                      </Text>
                    </div>
                    <div className="flex flex-col items-start">
                      {customer.email && !groupAppointment && (
                        <Text size="xs" c="dimmed">
                          {customer.email}
                        </Text>
                      )}
                      {customer.gsm && (
                        <Text size="xs" c="dimmed">
                          {customer.gsm}
                        </Text>
                      )}
                      {customer.comment && (
                        <div className="flex items-center gap-2 whitespace-pre-wrap rounded-md bg-[#f5f5f5] p-2">
                          <Text
                            size="xs"
                            c="dimmed"
                            style={{
                              whiteSpace: "normal",
                              overflowWrap: "break-word",
                            }}
                          >
                            {customer.comment}
                          </Text>
                        </div>
                      )}

                      {customer.extraDetails &&
                        customer.extraDetails.length > 0 && (
                          <div className="mt-1 w-full rounded-lg bg-[#F7F7F7] px-2 py-1">
                            <Text fw={500} size={"sm"}>
                              <Trans>Polja po meri</Trans>
                            </Text>
                            <div className="flex flex-col gap-1">
                              {customer.extraDetails.map((field) => {
                                let displayValue = "";

                                if (typeof field.value === "string") {
                                  displayValue = field.value;
                                } else if (
                                  field.type === "radio" ||
                                  field.type === "select"
                                ) {
                                  displayValue =
                                    field.value.en ||
                                    Object.values(field.value)[0];
                                }

                                const label =
                                  field.label.en ||
                                  Object.values(field.label)[0];

                                return (
                                  <div className="flex gap-1" key={field.id}>
                                    <Text size={"sm"} fw={500}>
                                      {label}:{" "}
                                    </Text>
                                    <Text size={"sm"}>{displayValue}</Text>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <Text size="sm" c="dimmed">
                <Trans>Na terminu še ni nobene stranke</Trans>
              </Text>
            )}
          </div>
        </>
      )}
    </div>
  );
};
